import React, { HTMLAttributeAnchorTarget, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'components/link';
import SplitType from 'split-type';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { buildClassName } from 'utils/build-class-name';
import Button, { PRIMARY_THEME } from 'components/control/button';
import styles from './style.module.scss';
import HtmlTrans, { HtmlTransClassNames } from 'components/html-trans';

import { useTranslation } from 'next-i18next';

const DEFAULT_AUTHOR_IMAGE_WIDTH = 195;
const DEFAULT_AUTHOR_IMAGE_HEIGHT = 160;
const HIGHLIGHT_CLASS_NAME = 'highlight';
const DEFAULT_CTA_TARGET = '_blank';
const DEFAULT_CTA_REL = 'noopener noreferrer';

/** The details of the author of the quote */
export type QuoteAuthor = {
  /** name of the author */
  name: string;
  /** title of the author */
  title: string;
  /** image of the author */
  image: string;
  /** alt text for the image of the author */
  imageAlt: string;
};

/** The details of the call-to-action button */
export type Cta = {
  /** label for the call-to-action button */
  label: string;
  /**  link for the call-to-action button. If the link is not provided, the button will not be displayed. */
  link: string;

  /** optional target for the call-to-action button. Default is '_blank' */
  target?: HTMLAttributeAnchorTarget | undefined;

  /** optional rel for the call-to-action button. Default is 'noopener noreferrer' */
  rel?: string | undefined;

  /** optional click handler for the call-to-action button */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

export type QuoteProps = {
  /** the details of the author of the quote */
  author: QuoteAuthor;
  /** the content of the quote */
  content: string;
  /** the call-to-action button */
  cta?: Cta;
  style?: {
    /** optional color for the highlight color */
    highlightColor?: string;
    /** optional width for the author image */
    authorImageWidth?: number | `${number}` | undefined;
    /** optional height for the author image */
    authorImageHeight?: number | `${number}` | undefined;
    /** optional class names for the html-trans component to override the default styles for the quote content.
     *
     * Note: The class name 'highlight' is added to the span elements of the quote content by default.
     */
    className?: HtmlTransClassNames;
  };
};

/**
 * Quote component to display a quote with author details and optional call-to-action with support for translation.
 */
export default function Quote({
  author,
  content,
  cta,
  style,
  children,
}: React.PropsWithChildren<QuoteProps>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();

  // Create animation setup
  useEffect(() => {
    let tl: gsap.core.Timeline;
    let typeSplit: SplitType;
    if (!containerRef.current) return;
    const localContainer = containerRef.current;

    if (document?.fonts) {
      document.fonts.ready.then(() => {
        const splitElement = localContainer.querySelector('.split-word');
        if (!splitElement) return;
        // Split the text up
        typeSplit = new SplitType(splitElement as HTMLElement, { types: 'lines,words' });

        splitElement.querySelectorAll('.word').forEach(word => {
          const lineMask = document.createElement('div');
          lineMask.className = 'line-mask';
          word.appendChild(lineMask);
        });

        // Create animation
        gsap.registerPlugin(ScrollTrigger);

        const allMasks = Array.from(splitElement.querySelectorAll('.word')).map(word => word.querySelector('.line-mask'));
        const allWords = splitElement.querySelectorAll('.word');

        tl = gsap.timeline({
          scrollTrigger: {
            trigger: splitElement,
            start: 'top center',
            end: 'bottom center',
            scrub: .5,
          },
        });

        gsap.set(allWords, {
          y: 10,
          opacity: 0.7,
        });

        tl.to(allMasks, {
          width: '0%',
          duration: 0.2,
          stagger: 0.1,
        }).to(allWords, {
          y: 0,
          opacity: 1,
          duration: 0.5,
          stagger: 0.1,
        }, 0);
      });
    }

    // Cleanup function
    return () => {
      // Clean up the animation when component unmounts
      if (tl) tl.kill();
      if (typeSplit) typeSplit.revert();
    };
  }, [i18n?.language]);

  const authorImageWidth = style?.authorImageWidth || DEFAULT_AUTHOR_IMAGE_WIDTH;
  const authorImageHeight = style?.authorImageHeight || DEFAULT_AUTHOR_IMAGE_HEIGHT;
  return (
    <div ref={containerRef} className={buildClassName(styles.component)} style={{ '--highlight-color': style?.highlightColor } as React.CSSProperties}>
      <div className={buildClassName(styles.container, 'quote-animation')}>
        <div className={styles.author}>
          <div className={styles['image-wrapper']}>
            <Image src={author.image} alt={author.imageAlt} width={authorImageWidth} height={authorImageHeight} />
          </div>
          <div className={styles['author-info']}>
            <p>{t(author.title)}</p>
            <h4>{author.name}</h4>
          </div>
        </div>
        <h3 className={'split-word'}>
          <span className={styles.spacer}/>
          <HtmlTrans 
            t={t} 
            i18nKey={content} 
            classNames={{ span: HIGHLIGHT_CLASS_NAME, ...style?.className }} 
            styleOverrides={{ span: { color: style?.highlightColor }}}
          />
        </h3>
        {cta &&
        <Link href={cta.link} target={cta.target || DEFAULT_CTA_TARGET} rel={cta.rel || DEFAULT_CTA_REL} onClick={cta.onClick}>
          <Button label={t(cta.label)} theme={PRIMARY_THEME} />
        </Link>
        }
        {children}
      </div>
    </div>
  );
}
